import { Typography } from "@libs/zapi-ui/custom";

import { useRef, useState, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";

export function ProjectDescription({ description }: { description?: string }) {
  const tooltipTriggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const isOverflowing = isTruncated;

  useEffect(() => {
    const triggerElement = tooltipTriggerRef.current;
    const descriptionElement = descriptionRef.current;

    if (!triggerElement || !descriptionElement) return;

    setTriggerWidth(triggerElement.offsetWidth);

    const checkTruncation = () => {
      if (descriptionElement) {
        const isTruncatedNow = descriptionElement.scrollWidth > descriptionElement.clientWidth;
        setIsTruncated(isTruncatedNow);
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === triggerElement) {
          const { width } = entry.contentRect;
          setTriggerWidth(width);
          setTimeout(checkTruncation, 0);
        }

        if (entry.target === descriptionElement) {
          setTimeout(checkTruncation, 0);
        }
      }
    });

    resizeObserver.observe(triggerElement);
    resizeObserver.observe(descriptionElement);

    return () => {
      resizeObserver.unobserve(triggerElement);
      resizeObserver.unobserve(descriptionElement);
      resizeObserver.disconnect();
    };
  }, [description]);

  if (!description) return null;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div ref={tooltipTriggerRef} className="relative">
          <Typography variant="body2" className="opacity-0">
            _
          </Typography>
          <div
            className="break-keep whitespace-nowrap overflow-hidden text-ellipsis min-w-0 flex-shrink absolute inset-0 text-left cursor-default font-normal text-[13px] leading-[22px]"
            ref={descriptionRef}
          >
            {description}
          </div>
        </div>
      </TooltipTrigger>
      {isOverflowing && (
        <TooltipContent
          side="bottom"
          style={{ maxWidth: triggerWidth ? `${triggerWidth}px` : "auto", minWidth: "400px" }}
        >
          <Typography variant="body2" className="break-words">
            {description}
          </Typography>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
